<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.DIALOG.PROMPT_REQUESTED"
    :handler="
      (data) => {
        $refs.modal.show();
      }
    "
  />

  <DialogComponent
    ref="modal"
    type="modal"
    mainClass="fixed z-[61]"
    @closed="
      () => {
        if (currentResolve) {
          currentResolve({ accepted: false });
          currentResolve = null;
        }
      }
    "
  >
    <div class="py-2">
      <div class="text-xl font-bold mb-5" v-if="promptData.title">
        {{ promptData.title }}
      </div>

      <div v-if="promptData.message" class="mb-5 text-slate-600">
        {{ promptData.message }}
      </div>

      <!-- Options (choice mode) -->
      <div
        v-if="promptData.options && promptData.options.length > 0"
        class="space-y-3 mb-5"
      >
        <div
          v-for="(option, index) in promptData.options"
          :key="index"
          @click="selectOption(option)"
          class="flex items-center gap-3 p-4 rounded-xl bg-slate-100 hover:bg-slate-200 cursor-pointer transition-colors"
          :class="{
            'ring-2 ring-orange-400':
              selectedOption && selectedOption.name === option.name
          }"
        >
          <div
            class="w-5 h-5 rounded-full border-2 border-slate-400 flex items-center justify-center"
          >
            <div
              v-if="selectedOption && selectedOption.name === option.name"
              class="w-3 h-3 bg-orange-500 rounded-full"
            ></div>
          </div>
          <div class="flex-1">
            <div class="font-medium text-slate-800">
              {{
                formatDisplay
                  ? formatDisplay(option)
                  : option.title || option.name || option.label
              }}
            </div>
            <div v-if="option.description" class="text-sm text-slate-600">
              {{ option.description }}
            </div>
          </div>
        </div>
      </div>

      <!-- Multiple inputs mode -->
      <div
        v-else-if="promptData.inputs && promptData.inputs.length > 0"
        class="space-y-4 mb-5"
      >
        <div
          v-for="(inputConfig, index) in promptData.inputs"
          :key="index"
          class="space-y-1"
        >
          <label
            v-if="inputConfig.label"
            class="block text-sm font-medium text-slate-700"
          >
            {{ inputConfig.label }}
            <span v-if="inputConfig.required" class="text-red-500">*</span>
          </label>

          <!-- Text input -->
          <input
            v-if="
              !inputConfig.type ||
              ['text', 'tel', 'email', 'number', 'url'].includes(
                inputConfig.type
              )
            "
            v-model="inputValues[inputConfig.key || inputConfig.name || index]"
            :type="inputConfig.type || 'text'"
            :placeholder="inputConfig.placeholder || ''"
            :required="inputConfig.required || false"
            :min="inputConfig.min"
            :max="inputConfig.max"
            :step="inputConfig.step"
            :pattern="inputConfig.pattern"
            :disabled="inputConfig.disabled || false"
            class="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
            :class="{
              'border-red-400': getFieldError(
                inputConfig.key || inputConfig.name || index
              ),
              'bg-slate-100': inputConfig.disabled
            }"
          />

          <!-- Textarea -->
          <textarea
            v-else-if="inputConfig.type === 'textarea'"
            v-model="inputValues[inputConfig.key || inputConfig.name || index]"
            :placeholder="inputConfig.placeholder || ''"
            :required="inputConfig.required || false"
            :rows="inputConfig.rows || 3"
            :disabled="inputConfig.disabled || false"
            class="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all resize-none"
            :class="{
              'border-red-400': getFieldError(
                inputConfig.key || inputConfig.name || index
              ),
              'bg-slate-100': inputConfig.disabled
            }"
          ></textarea>

          <!-- Select dropdown -->
          <select
            v-else-if="inputConfig.type === 'select'"
            v-model="inputValues[inputConfig.key || inputConfig.name || index]"
            :required="inputConfig.required || false"
            :disabled="inputConfig.disabled || false"
            class="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all bg-white"
            :class="{
              'border-red-400': getFieldError(
                inputConfig.key || inputConfig.name || index
              ),
              'bg-slate-100': inputConfig.disabled
            }"
          >
            <option value="" disabled selected>
              {{ inputConfig.placeholder || 'Sélectionner...' }}
            </option>
            <option
              v-for="opt in inputConfig.options"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label || opt.value }}
            </option>
          </select>

          <!-- Checkbox -->
          <div
            v-else-if="inputConfig.type === 'checkbox'"
            class="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <input
              :id="`input-${index}`"
              type="checkbox"
              v-model="
                inputValues[inputConfig.key || inputConfig.name || index]
              "
              :disabled="inputConfig.disabled || false"
              class="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-400"
            />
            <label
              :for="`input-${index}`"
              class="flex-1 text-sm text-slate-700 cursor-pointer"
            >
              {{ inputConfig.checkboxLabel || inputConfig.label }}
            </label>
          </div>

          <!-- Error message -->
          <div
            v-if="getFieldError(inputConfig.key || inputConfig.name || index)"
            class="text-sm text-red-500"
          >
            {{ getFieldError(inputConfig.key || inputConfig.name || index) }}
          </div>

          <!-- Helper text -->
          <div
            v-if="
              inputConfig.helper &&
              !getFieldError(inputConfig.key || inputConfig.name || index)
            "
            class="text-xs text-slate-500"
          >
            {{ inputConfig.helper }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex gap-3 mt-6 justify-end">
      <button
        class="px-6 py-3 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors font-medium"
        @click="cancelPrompt"
      >
        {{ promptData.cancelText || 'Annuler' }}
      </button>
      <button
        class="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-primary-500 text-white hover:from-orange-600 hover:to-primary-600 transition-all font-medium shadow-md shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canConfirm"
        @click="confirmPrompt"
      >
        {{ promptData.successText || 'Confirmer' }}
      </button>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();

const promptData = ref({
  title: '',
  message: '',
  options: [],
  inputs: [],
  placeholder: '',
  successText: '',
  cancelText: 'Annuler',
  formatDisplay: null
});

const selectedOption = ref(null);
const inputValues = ref({});
const fieldErrors = ref({});
let currentResolve = null;
let currentReject = null;

const formatDisplay = ref(null);

// Check if prompt can be confirmed
const canConfirm = computed(() => {
  // If options mode, need selected option
  if (promptData.value.options && promptData.value.options.length > 0) {
    return selectedOption.value !== null;
  }

  // If inputs mode, check required fields
  if (promptData.value.inputs && promptData.value.inputs.length > 0) {
    return promptData.value.inputs.every((input) => {
      const key = input.key || input.name;
      const value = inputValues.value[key];

      // If not required, always valid
      if (!input.required) return true;

      // If required, check if value exists
      if (input.type === 'checkbox') {
        return value === true;
      }

      return value !== undefined && value !== null && value !== '';
    });
  }

  // Simple confirm mode (no inputs, no options)
  return true;
});

const getFieldError = (key) => {
  return fieldErrors.value[key];
};

const validateField = (inputConfig) => {
  const key = inputConfig.key || inputConfig.name;
  const value = inputValues.value[key];

  // Clear previous error
  delete fieldErrors.value[key];

  // Required validation
  if (
    inputConfig.required &&
    (value === undefined || value === null || value === '')
  ) {
    if (inputConfig.type !== 'checkbox') {
      fieldErrors.value[key] =
        inputConfig.requiredMessage || 'Ce champ est requis';
      return false;
    }
  }

  // Pattern validation
  if (value && inputConfig.pattern) {
    const regex = new RegExp(inputConfig.pattern);
    if (!regex.test(value)) {
      fieldErrors.value[key] = inputConfig.patternMessage || 'Format invalide';
      return false;
    }
  }

  // Min/max for numbers
  if (value && inputConfig.type === 'number') {
    const numValue = Number(value);
    if (inputConfig.min !== undefined && numValue < inputConfig.min) {
      fieldErrors.value[key] = `Minimum: ${inputConfig.min}`;
      return false;
    }
    if (inputConfig.max !== undefined && numValue > inputConfig.max) {
      fieldErrors.value[key] = `Maximum: ${inputConfig.max}`;
      return false;
    }
  }

  // Min/max length for text
  if (value && typeof value === 'string') {
    if (inputConfig.minLength && value.length < inputConfig.minLength) {
      fieldErrors.value[key] = `Minimum ${inputConfig.minLength} caractères`;
      return false;
    }
    if (inputConfig.maxLength && value.length > inputConfig.maxLength) {
      fieldErrors.value[key] = `Maximum ${inputConfig.maxLength} caractères`;
      return false;
    }
  }

  return true;
};

const selectOption = (option) => {
  selectedOption.value = option;
};

const confirmPrompt = () => {
  if (currentResolve) {
    let resultData;

    if (promptData.value.options && promptData.value.options.length > 0) {
      // Options mode
      resultData = selectedOption.value;
    } else if (promptData.value.inputs && promptData.value.inputs.length > 0) {
      // Inputs mode - validate first
      const isValid = promptData.value.inputs.every((input) =>
        validateField(input)
      );

      if (!isValid) {
        return; // Don't close if validation fails
      }

      resultData = inputValues.value;
    }

    currentResolve({
      accepted: true,
      data: resultData
    });
    currentResolve = null;
    currentReject = null;
  }
  resetPrompt();
  proxy.$refs.modal?.hide();
};

const cancelPrompt = () => {
  if (currentResolve) {
    currentResolve({ accepted: false });
    currentResolve = null;
    currentReject = null;
  }
  resetPrompt();
  proxy.$refs.modal?.hide();
};

const resetPrompt = () => {
  promptData.value = {
    title: '',
    message: '',
    options: [],
    inputs: [],
    placeholder: '',
    successText: '',
    cancelText: 'Annuler',
    formatDisplay: null
  };
  selectedOption.value = null;
  inputValues.value = {};
  fieldErrors.value = {};
  formatDisplay.value = null;
};

const customPrompt = async (config) => {
  return new Promise((resolve, reject) => {
    currentResolve = resolve;
    currentReject = reject;

    promptData.value = {
      title: config.title || '',
      message: config.message || '',
      inputs: config.inputs || [],
      placeholder: config.placeholder || '',
      successText: config.successText || 'Confirmer',
      cancelText: config.cancelText || 'Annuler',
      options: [],
      formatDisplay: null
    };

    // Initialize input values with defaults
    if (config.inputs && config.inputs.length > 0) {
      inputValues.value = {};
      config.inputs.forEach((input, index) => {
        const key = input.key || input.name || index;
        inputValues.value[key] =
          input.defaultValue !== undefined ? input.defaultValue : '';
      });
    }

    proxy.$refs.modal?.show();
  });
};

const choicePrompt = async (config) => {
  return new Promise((resolve, reject) => {
    currentResolve = resolve;
    currentReject = reject;

    promptData.value = {
      title: config.title || '',
      message: config.message || '',
      options: config.options || [],
      placeholder: config.placeholder || '',
      successText: config.successText || 'Confirmer',
      cancelText: config.cancelText || 'Annuler',
      inputs: [],
      formatDisplay: config.formatDisplay || null
    };

    formatDisplay.value = config.formatDisplay || null;
    selectedOption.value = null;
    proxy.$refs.modal?.show();
  });
};

const confirmDialog = async (config) => {
  return new Promise((resolve, reject) => {
    currentResolve = resolve;
    currentReject = reject;

    promptData.value = {
      title: config.title || '',
      message: config.message || '',
      options: [],
      inputs: [],
      successText: config.successText || 'OK',
      cancelText: config.cancelText || null,
      formatDisplay: null
    };

    proxy.$refs.modal?.show();
  });
};

// Exposer les fonctions globalement
onMounted(() => {
  window.customPrompt = customPrompt;
  window.choicePrompt = choicePrompt;
  window.confirmDialog = confirmDialog;
});

onBeforeUnmount(() => {
  delete window.customPrompt;
  delete window.choicePrompt;
  delete window.confirmDialog;
});
</script>

<!--
╔══════════════════════════════════════════════════════════════════════════════════════════════════════╗
║                                  PROMPT DIALOG - GUIDE D'UTILISATION                                ║
╚══════════════════════════════════════════════════════════════════════════════════════════════════════╝

Ce composant fournit 3 fonctions globales pour afficher des boîtes de dialogue modales.

══════════════════════════════════════════════════════════════════════════════════════════════════════
1. CONFIRM DIALOG - Simple confirmation (sans inputs)
══════════════════════════════════════════════════════════════════════════════════════════════════════

  const result = await window.confirmDialog({
    title: 'Confirmer la suppression',
    message: 'Voulez-vous vraiment supprimer cet élément ?',
    successText: 'Supprimer',
    cancelText: 'Annuler'  // null pour masquer le bouton annuler
  });

  if (result.accepted) {
    // L'utilisateur a confirmé
  }

══════════════════════════════════════════════════════════════════════════════════════════════════════
2. CHOICE PROMPT - Sélection parmi des options
══════════════════════════════════════════════════════════════════════════════════════════════════════

  const result = await window.choicePrompt({
    title: 'Mode de livraison',
    message: 'Choisissez comment vous souhaitez être livré',
    successText: 'Valider',
    cancelText: 'Annuler',
    options: [
      {
        name: 'delivery',
        title: 'Livraison à domicile',
        description: 'Reçu dans 30-45 minutes',
        value: { mode: 'delivery', fee: 500 }
      },
      {
        name: 'pickup',
        title: 'Retrait sur place',
        description: 'Prêt en 15-20 minutes',
        value: { mode: 'pickup', fee: 0 }
      }
    ],
    // Optionnel: fonction pour formater l'affichage
    formatDisplay: (option) => `${option.title} - ${option.description}`
  });

  if (result.accepted) {
    console.log(result.data);  // L'option sélectionnée
  }

══════════════════════════════════════════════════════════════════════════════════════════════════════
3. CUSTOM PROMPT - Formulaire avec champs de saisie
══════════════════════════════════════════════════════════════════════════════════════════════════════

  const result = await window.customPrompt({
    title: 'Informations de livraison',
    message: 'Veuillez remplir les informations ci-dessous',
    successText: 'Confirmer',
    cancelText: 'Annuler',
    inputs: [
      // ─── CHAMP TEXT ───────────────────────────────────────────────────────────────────────────
      {
        key: 'fullName',                    // Identifiant unique (requis)
        type: 'text',                       // text, tel, email, number, url, textarea, select, checkbox
        label: 'Nom complet',               // Libellé du champ
        placeholder: 'Jean Dupont',         // Texte de suggestion
        required: true,                     // Champ obligatoire
        minLength: 2,                       // Longueur minimum
        maxLength: 50,                      // Longueur maximum
        defaultValue: '',                   // Valeur par défaut
        helper: 'Entrez votre nom et prénom'  // Texte d'aide
      },

      // ─── CHAMP TÉLÉPHONE ───────────────────────────────────────────────────────────────────────────
      {
        key: 'phone',
        type: 'tel',
        label: 'Téléphone',
        placeholder: '+229 XX XX XX XX',
        required: true,
        pattern: '^[0-9]{8,15}$',           // Regex de validation
        patternMessage: 'Format invalide (8-15 chiffres)'  // Message d'erreur personnalisé
      },

      // ─── CHAMP EMAIL ────────────────────────────────────────────────────────────────────────────
      {
        key: 'email',
        type: 'email',
        label: 'Email',
        placeholder: 'exemple@email.com',
        required: false,
        pattern: '^[^@]+@[^@]+\\.[^@]+$'
      },

      // ─── CHAMP NUMBER ────────────────────────────────────────────────────────────────────────────
      {
        key: 'age',
        type: 'number',
        label: 'Âge',
        placeholder: '18',
        required: true,
        min: 18,                             // Valeur minimum
        max: 120,                            // Valeur maximum
        step: 1                              // Incrément
      },

      // ─── TEXTAREA ────────────────────────────────────────────────────────────────────────────────
      {
        key: 'address',
        type: 'textarea',
        label: 'Adresse complète',
        placeholder: 'Votre adresse...',
        required: true,
        rows: 3                              // Nombre de lignes
      },

      // ─── SELECT DROPDOWN ───────────────────────────────────────────────────────────────────────────
      {
        key: 'city',
        type: 'select',
        label: 'Ville',
        placeholder: 'Sélectionner une ville',
        required: true,
        options: [                           // Options du select
          { value: 'cotonou', label: 'Cotonou' },
          { value: 'porto-novo', label: 'Porto-Novo' },
          { value: 'parakou', label: 'Parakou' }
        ]
      },

      // ─── CHECKBOX ───────────────────────────────────────────────────────────────────────────────────
      {
        key: 'termsAccepted',
        type: 'checkbox',
        checkboxLabel: 'J\'accepte les conditions générales',  // Label pour checkbox
        defaultValue: false,
        required: true,
        requiredMessage: 'Vous devez accepter les conditions'
      }
    ]
  });

  if (result.accepted) {
    console.log(result.data);
    // {
    //   fullName: 'Jean Dupont',
    //   phone: '+229XXXXXXXX',
    //   email: 'exemple@email.com',
    //   age: 25,
    //   address: 'Mon adresse...',
    //   city: 'cotonou',
    //   termsAccepted: true
    // }
  }

══════════════════════════════════════════════════════════════════════════════════════════════════════
4. CONFIGURATION DES INPUTS - RÉFÉRENCE COMPLÈTE
══════════════════════════════════════════════════════════════════════════════════════════════════════

  Propriétés disponibles pour chaque input:

  ┌────────────────────┬──────────┬────────────────────────────────────────────────────────────────┐
  │ Propriété          │ Type     │ Description                                                    │
  ├────────────────────┼──────────┼────────────────────────────────────────────────────────────────┤
  │ key (ou name)      │ string   │ Identifiant unique du champ (requis)                           │
  │ type               │ string   │ text, tel, email, number, url, textarea, select, checkbox     │
  │ label              │ string   │ Libellé affiché au-dessus du champ                             │
  │ checkboxLabel      │ string   │ Libellé pour les checkboxes (à la place de label)              │
  │ placeholder        │ string   │ Texte de suggestion dans le champ                              │
  │ defaultValue       │ any      │ Valeur par défaut du champ                                     │
  │ required           │ boolean  │ Le champ est obligatoire                                       │
  │ disabled           │ boolean  │ Le champ est désactivé                                         │
  │ helper             │ string   │ Texte d'aide affiché sous le champ                             │
  │ min                │ number   │ Valeur minimum (pour number)                                   │
  │ max                │ number   │ Valeur maximum (pour number)                                   │
  │ step               │ number   │ Incrément (pour number)                                        │
  │ minLength          │ number   │ Longueur minimum (pour text)                                   │
  │ maxLength          │ number   │ Longueur maximum (pour text)                                   │
  │ pattern            │ string   │ Regex de validation                                            │
  │ rows               │ number   │ Nombre de lignes (pour textarea)                               │
  │ options            │ array    │ Options pour select [{value, label}]                           │
  │ requiredMessage    │ string   │ Message d'erreur personnalisé (champ requis)                    │
  │ patternMessage     │ string   │ Message d'erreur personnalisé (pattern invalide)                │
  └────────────────────┴──────────┴────────────────────────────────────────────────────────────────┘

══════════════════════════════════════════════════════════════════════════════════════════════════════
5. EXEMPLE PRATIQUE - CARTBOTTOMSHEET
══════════════════════════════════════════════════════════════════════════════════════════════════════

  const confirmRemoveItem = async (dish) => {
    try {
      const result = await window.customPrompt({
        title: 'Retirer du panier',
        message: `Voulez-vous retirer ${dish.title || dish.dishName} de votre panier ?`,
        successText: 'Retirer du panier',
        cancelText: 'Retour'
      });

      if (result?.accepted) {
        emit('removeItem', dish.id || dish._id);
      }
    } catch (error) {
      console.error('Error showing prompt:', error);
    }
  };

══════════════════════════════════════════════════════════════════════════════════════════════════════
6. EXEMPLE PRATIQUE - FORMULAIRE DE LIVRAISON
══════════════════════════════════════════════════════════════════════════════════════════════════════

  const requestDeliveryInfo = async () => {
    const result = await window.customPrompt({
      title: 'Informations de livraison',
      message: 'Nous avons besoin de quelques informations pour votre livraison',
      successText: 'Valider',
      inputs: [
        {
          key: 'contactName',
          type: 'text',
          label: 'Nom du contact',
          placeholder: 'Votre nom',
          required: true,
          minLength: 2,
          helper: 'Le nom de la personne qui recevra la livraison'
        },
        {
          key: 'contactPhone',
          type: 'tel',
          label: 'Téléphone de contact',
          placeholder: '+229 XX XX XX XX',
          required: true,
          pattern: '^[0-9]{8,15}$',
          patternMessage: 'Numéro invalide (8-15 chiffres requis)'
        },
        {
          key: 'instructions',
          type: 'textarea',
          label: 'Instructions de livraison (optionnel)',
          placeholder: 'Maison bleue, près du marché...',
          rows: 2
        }
      ]
    });

    if (result?.accepted) {
      const { contactName, contactPhone, instructions } = result.data;
      // Utiliser les données...
    }
  };

══════════════════════════════════════════════════════════════════════════════════════════════════════
-->
